import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, Radar, BadgeCheck, Ban, ExternalLink, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { investmentsAPI } from '../../lib/api';
import { fmtEur, fmtDate, PaymentStatusBadge, CryptoChip } from './investmentShared';

const Row = ({ k, v, ok }) => (
    <div className="flex items-center justify-between gap-3 text-xs">
        <span className="text-slate-400">{k}</span>
        <span className={`font-semibold ${ok === true ? 'text-emerald-300' : ok === false ? 'text-red-300' : 'text-white'}`}>{v}</span>
    </div>
);

const OnchainResult = ({ r, expected }) => {
    if (!r) return null;
    if (r.found === false || r.found === null) {
        return <p className="text-amber-300 text-xs flex items-center gap-1.5" data-testid="onchain-not-found"><AlertTriangle className="w-3.5 h-3.5" /> {r.message}</p>;
    }
    return (
        <div className="space-y-1.5" data-testid="onchain-result">
            <Row k="Transacción encontrada" v="Sí" ok />
            <Row k="Confirmada" v={r.confirmed ? `Sí · ${r.confirmations} conf.` : 'Aún sin confirmar'} ok={r.confirmed} />
            <Row k="Paga a la wallet de plataforma" v={r.pays_expected_address ? 'Sí' : 'No'} ok={r.pays_expected_address} />
            <Row k={`Importe recibido (${r.asset})`} v={Number(r.amount_to_expected || 0).toLocaleString('de-DE', { maximumFractionDigits: 8 })} />
            {r.block_time && <Row k="Fecha en cadena" v={fmtDate(r.block_time * 1000)} />}
            {expected && <p className="text-[10.5px] text-slate-500 font-mono break-all">Wallet esperada: {expected}</p>}
        </div>
    );
};

export const PaymentReviewDialog = ({ request, onClose, onChanged }) => {
    const [checking, setChecking] = useState(false);
    const [onchain, setOnchain] = useState(request?.payment?.onchain || null);
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const p = request?.payment;

    const check = async () => {
        setChecking(true);
        try {
            const { data } = await investmentsAPI.adminOnchain(request.id);
            setOnchain(data.onchain);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al consultar la cadena');
        } finally {
            setChecking(false);
        }
    };

    const review = async (action) => {
        if (action === 'reject' && reason.trim().length < 5) { toast.error('Indica el motivo del rechazo'); return; }
        setSubmitting(true);
        try {
            await investmentsAPI.adminReviewPayment(request.id, { action, reason: reason.trim() });
            toast.success(action === 'verify' ? 'Pago verificado y usuario notificado' : 'Pago rechazado y usuario notificado');
            onChanged?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al registrar la revisión');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-white" data-testid="admin-payment-dialog">
                <DialogHeader><DialogTitle className="text-white">Verificar pago en cadena</DialogTitle></DialogHeader>
                {request && p && (
                    <div className="space-y-4 py-1">
                        <p className="text-slate-400 text-sm">{request.user_name} · <span className="text-white font-semibold">{fmtEur(request.amount)}</span> · <CryptoChip crypto={request.crypto} /></p>
                        <div className="rounded-lg bg-slate-950 border border-slate-800 px-3 py-2.5 space-y-2">
                            <div className="flex flex-wrap items-center gap-2"><PaymentStatusBadge payment={p} /><span className="text-slate-500 text-[11px]">Enviado {fmtDate(p.submitted_at)}</span></div>
                            <p className="font-mono text-[11.5px] text-white break-all" data-testid="admin-payment-hash">{p.tx_hash}</p>
                            <a href={p.explorer_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#7CB1E5] hover:text-white" data-testid="admin-payment-explorer">
                                Abrir en el explorador <ExternalLink className="w-3 h-3" />
                            </a>
                        </div>
                        <div className="rounded-lg bg-slate-950 border border-slate-800 px-3 py-2.5 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                                <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400">Comprobación on-chain</p>
                                <Button size="sm" onClick={check} disabled={checking} className="h-7 bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 text-xs" data-testid="admin-payment-check-btn">
                                    {checking ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Radar className="w-3.5 h-3.5 mr-1" />} {onchain ? 'Volver a comprobar' : 'Comprobar'}
                                </Button>
                            </div>
                            {onchain ? <OnchainResult r={onchain} expected={p.expected_address} /> : <p className="text-slate-600 text-xs">Consulta mempool.space (BTC) o Tronscan (USDT TRC20) para validar la transacción.</p>}
                        </div>
                        {p.status === 'submitted' && (
                            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (obligatorio solo si rechazas)"
                                className="bg-slate-950 border-slate-800 text-white text-sm min-h-[70px]" data-testid="admin-payment-reason-input" />
                        )}
                        {p.status !== 'submitted' && p.reject_reason && <p className="text-red-300 text-xs">Motivo del rechazo: {p.reject_reason}</p>}
                    </div>
                )}
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cerrar</Button>
                    {p?.status === 'submitted' && (
                        <>
                            <Button onClick={() => review('reject')} disabled={submitting} className="bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25" data-testid="admin-payment-reject-btn">
                                {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <XCircle className="w-4 h-4 mr-1.5" />} Rechazar pago
                            </Button>
                            <Button onClick={() => review('verify')} disabled={submitting} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold" data-testid="admin-payment-verify-btn">
                                {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />} Confirmar pago verificado
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const PaymentCell = ({ request, onReview }) => {
    const p = request.payment;
    if (!p) return null;
    const Icon = p.status === 'verified' ? BadgeCheck : p.status === 'rejected' ? Ban : Radar;
    return (
        <button type="button" onClick={() => onReview(request)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-violet-500/40 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20 text-xs font-semibold"
            data-testid={`admin-investment-payment-${request.id}`}>
            <Icon className="w-3.5 h-3.5" /> {p.status === 'submitted' ? 'Verificar pago' : p.status === 'verified' ? 'Pago verificado' : 'Pago rechazado'}
        </button>
    );
};
